/**
 * URL Router Module
 * Handles URL-based navigation and deep linking.
 *
 * Two URL schemes:
 *   Spot (canonical): /{slug} — pretty URL, one per spot (also ?spot={slug})
 *   Station (legacy): ?region=..&area=..&station=.. — still parsed, then
 *                     normalized to the spot form by app.js
 */

import { AppState, findLocationForStation } from './state.js';
import { analytics, trackPageView, setLocationContext } from './analytics.js';
import { parseRouteParts } from './route-utils.js';
import { formatRegionName, formatAreaName } from '../shared/location-names.mjs';

/**
 * Parse the current URL for spot, region, area, and station parameters
 * @returns {Object} Parsed URL parameters (spot includes path-slug form)
 */
export function parseUrl() {
    const { pathSlug, spot, region, area, station } = parseRouteParts(window.location);

    return {
        // Path slug wins over ?spot= (it is the canonical form)
        spot: pathSlug || spot,
        pathSlug,
        region,
        area,
        station
    };
}

/**
 * Update the URL to the canonical pretty form for a spot (/{slug})
 * @param {string} slug - Spot slug
 * @param {boolean} replaceState - Whether to replace current history entry
 */
export function updateSpotUrl(slug, replaceState = false) {
    if (!slug) return;

    const url = `/${slug}`;
    const state = { spotSlug: slug };

    if (replaceState) {
        history.replaceState(state, '', url);
    } else {
        history.pushState(state, '', url);
    }

    // Analytics: spot-aware virtual page view + location context
    const spot = AppState.currentSpot;
    const spotName = spot?.name || slug;
    const regionName = spot?.region ? formatRegionName(spot.region) : '';
    const areaName = spot?.area ? formatAreaName(spot.area) : '';

    analytics.spotChange(slug, spotName, spot?.region || '');
    trackPageView(`/spot/${slug}`, `${spotName} | Surf Report`);
    setLocationContext(regionName, areaName, spotName);
}

/**
 * Update the URL without triggering a page reload (legacy station mode)
 * Uses clean URLs - only adds params when not using defaults
 * @param {string} regionId - Region ID
 * @param {string} areaId - Area ID
 * @param {string} stationId - Station ID
 * @param {boolean} replaceState - Whether to replace current history entry
 */
export function updateUrl(regionId, areaId, stationId, replaceState = false) {
    const params = new URLSearchParams();

    const region = AppState.regions[regionId];
    const area = region?.areas?.[areaId];

    // Only add region if not the default
    const defaultRegion = Object.keys(AppState.regions)[0] || 'california';
    if (regionId && regionId !== defaultRegion) {
        params.set('region', regionId);
    }

    // Only add area if not the region's default
    if (areaId && region && areaId !== region.defaultArea) {
        params.set('area', areaId);
    }

    // Only add station if not the area's default
    if (stationId && area && stationId !== area.defaultStation) {
        params.set('station', stationId);
    }

    // Build the new URL (always from root — the current pathname may be a
    // spot slug, which must not leak into legacy URLs)
    const paramString = params.toString();
    const url = paramString ? `/?${paramString}` : '/';

    // Update browser history
    const state = { regionId, areaId, stationId };
    if (replaceState) {
        history.replaceState(state, '', url);
    } else {
        history.pushState(state, '', url);
    }

    // Track location change in analytics
    const station = area?.stations?.[stationId];
    const regionName = region?.name || regionId;
    const areaName = area?.name || areaId;
    const stationName = station?.name || stationId;

    analytics.locationChange(regionName, areaName, stationName);

    // Track as virtual page view for SPA navigation
    trackPageView(
        `/${regionId}/${areaId}/${stationId}`,
        `${stationName} | Surf Report`
    );

    // Update user properties for location context
    setLocationContext(regionName, areaName, stationName);
}

/**
 * Initialize URL routing and set up navigation listeners
 * @param {Function} onRoute - Callback receiving a route object:
 *        { mode: 'spot', slug }
 *        { mode: 'station', region, area, station }
 *        { mode: 'default' }
 */
export function initRouter(onRoute) {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', (event) => {
        // History entries written by updateSpotUrl carry a spotSlug
        if (event.state?.spotSlug) {
            onRoute({ mode: 'spot', slug: event.state.spotSlug });
            return;
        }

        // Legacy history entries written by updateUrl
        if (event.state?.regionId || event.state?.stationId) {
            onRoute({
                mode: 'station',
                region: event.state.regionId,
                area: event.state.areaId,
                station: event.state.stationId
            });
            return;
        }

        // No state (direct URL access / older entries) - parse the URL fresh
        const { spot, region, area, station } = parseUrl();
        if (spot) {
            onRoute({ mode: 'spot', slug: spot });
        } else if (region || area || station) {
            onRoute({ mode: 'station', region, area, station });
        } else {
            onRoute({ mode: 'default' });
        }
    });
}

/**
 * Determine the initial region, area, and station from URL or defaults
 * Handles legacy region IDs and station-only URLs
 * @param {Object} regionsData - Full regions configuration
 * @returns {Object} Initial { regionId, areaId, stationId }
 */
export function getInitialState(regionsData) {
    const { region, area, station } = parseUrl();
    return resolveStationHierarchy(regionsData, region, area, station);
}

/**
 * Resolve raw region/area/station params against the regions registry,
 * applying legacy mappings and defaults.
 * @param {Object} regionsData - Full regions configuration
 * @param {string|null} region - Raw region param
 * @param {string|null} area - Raw area param
 * @param {string|null} station - Raw station param
 * @returns {Object} { regionId, areaId, stationId }
 */
export function resolveStationHierarchy(regionsData, region, area, station) {
    let regionId = region;
    let areaId = area;
    let stationId = station;

    // If station provided without region/area, find the full path
    if (stationId && !regionId) {
        const location = findLocationForStation(stationId, regionsData.regions);
        if (location) {
            regionId = location.regionId;
            areaId = location.areaId;
        }
    }

    // Check for legacy region mapping (e.g., 'socal' → 'california')
    if (regionId && !regionsData.regions[regionId]) {
        const mapping = regionsData._legacyMapping?.[regionId];
        if (mapping) {
            regionId = mapping.region;
            areaId = areaId || mapping.area;
        }
    }

    // Fall back to defaults
    if (!regionId || !regionsData.regions[regionId]) {
        regionId = regionsData.defaultRegion || 'california';
    }

    const regionObj = regionsData.regions[regionId];

    // If no area specified, use region's default
    if (!areaId || !regionObj?.areas?.[areaId]) {
        areaId = regionObj?.defaultArea || Object.keys(regionObj?.areas || {})[0];
    }

    const areaObj = regionObj?.areas?.[areaId];

    // If no station specified, use area's default
    if (!stationId || !areaObj?.stations?.[stationId]) {
        stationId = areaObj?.defaultStation;
    }

    return { regionId, areaId, stationId };
}

/**
 * Generate a shareable URL for the current location
 * Spot mode: the canonical pretty URL (/{slug})
 * Legacy mode: ALWAYS includes station for explicit deep linking
 * @param {string} regionId - Region ID
 * @param {string} areaId - Area ID
 * @param {string} stationId - Station ID
 * @returns {string} Full URL string
 */
export function generateShareUrl(regionId, areaId, stationId) {
    // Spot mode: share the canonical pretty URL
    if (AppState.useSpotMode && AppState.currentSpotSlug) {
        return `${window.location.origin}/${AppState.currentSpotSlug}`;
    }

    const params = new URLSearchParams();

    const region = AppState.regions[regionId];

    // ALWAYS include station for explicit share links (most important for deep linking)
    if (stationId) {
        params.set('station', stationId);
    }

    // Include region only if not default (for cleaner URLs)
    const defaultRegion = Object.keys(AppState.regions)[0] || 'california';
    if (regionId && regionId !== defaultRegion) {
        params.set('region', regionId);
    }

    // Include area only if not region's default
    if (areaId && region && areaId !== region.defaultArea) {
        params.set('area', areaId);
    }

    const paramString = params.toString();
    const baseUrl = `${window.location.origin}/`;

    return paramString ? `${baseUrl}?${paramString}` : baseUrl;
}
